import { Box, Button, Grid, styled, useTheme } from '@mui/material';
import CustomFormLabel from '../forms/theme-elements/CustomFormLabel';
import CustomTextField from '../forms/theme-elements/CustomTextField';
import Mustache from 'mustache';
import { t } from 'i18next';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CampaignToolsFormValue } from '@features/Admin/Campaign/model/type';
import { fromPairs } from 'lodash';
import { InfoDialog } from '../infoDialog/infoDialog';
import { StyledErrorText } from '../errorText';
import { IconEye } from '@shared/assets/images/svgs';
import { BorderedGrid } from '../borderedGrid';
import { firstSymbolToUpperCase } from '@shared/layouts/FullLayout/shared/breadcrumb/utils';
import { CustomTooltip } from '../customTooltip';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import { CampaignStatus } from '@shared/services/apiService';

type mastecheValues = {
  agentName: string;
  templateTextFieldValue: string;
  campaignName: string;
  companyName?: string;
};

interface MastecheType {
  key: string;
  value?: string;
}

interface CampaignTextField {
  mastecheValues: mastecheValues;
  setTemplateTextFieldValue: React.Dispatch<React.SetStateAction<string>>;
  isCampaignActive: CampaignStatus;
  disabled: boolean;
}

const StyledMastecheButton = styled(Button)(({ theme }) => ({
  margin: '4px 0 4px 4px',
  borderRadius: '4px',
  fontSize: '12px',
  color: theme.palette.extendedGrey?.dark,
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    color: theme.palette.extendedGrey?.dark,
    backgroundColor: theme.palette.extendedGrey?.neutralLight,
    boxShadow: `0px 1px 3px 0px ${theme.palette.extendedGrey?.neutralLight}`,
  },
}));

const IconButton = styled(Box)(({ theme }) => ({
  right: '16px',
  top: '16px',
  position: 'absolute',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.extendedGrey?.lightest,
  borderRadius: '8px',
}));

export const CampaignTextField = ({
  mastecheValues,
  setTemplateTextFieldValue,
  isCampaignActive,
  disabled,
}: CampaignTextField) => {
  const theme = useTheme();
  const {
    control: toolsControl,
    formState: { errors: formStateErrors },
    setValue,
    getValues,
  } = useFormContext<CampaignToolsFormValue>();

  const [openModal, setOpenModal] = React.useState(false);
  const [renderedTextFieldValue, setRenderedTextFieldValue] = useState('');
  Mustache.tags = ['[[%', '%]]'];

  const requireTemplateTextField = getValues('sendFollowUpMessageTool.enabled');
  const mastecheList: MastecheType[] = [
    { key: 'agent_human_name', value: mastecheValues.agentName },
    { key: 'company_name', value: mastecheValues.companyName },
    { key: 'campaign_name', value: mastecheValues.campaignName },
  ];

  const mapMastecheValues = (array: MastecheType[]) =>
    fromPairs(array.map(({ key, value }) => [key, value]));
  const wrapMastecheTags = (text: string) => '[[%' + text + '%]]';

  const getTextFromMastecheTag = (text: string, truncate: boolean = true) => {
    const parsedText = firstSymbolToUpperCase(text.replaceAll('_', ' '));
    return parsedText.length > 26 && truncate ? parsedText.slice(0, 26) + '...' : parsedText;
  };

  const insertAtCursor = (input: HTMLTextAreaElement, text: string) => {
    const cursorPos = getCursorPosition(input);
    const wrappedText = wrapMastecheTags(text);
    const currentValue = getValues('sendFollowUpMessageTool.messageTemplate') || '';
    const newValue = currentValue.slice(0, cursorPos) + wrappedText + currentValue.slice(cursorPos);

    setValue('sendFollowUpMessageTool.messageTemplate', newValue, { shouldDirty: true });

    //setTimeout used here to set cursor after updating input by react-hook-forms
    setTimeout(() => {
      input.setSelectionRange(cursorPos + wrappedText.length, cursorPos + wrappedText.length);
      input.focus();
    }, 0);
  };

  const handleAddMastecheValue = (key: string) => {
    if (isCampaignActive !== CampaignRunStatusType.INACTIVE) return;
    const input = document.getElementById('campaign-template') as HTMLTextAreaElement;
    if (input) {
      insertAtCursor(input, key);
    }
  };

  const getCursorPosition = (input: HTMLTextAreaElement) => {
    if ('selectionStart' in input) {
      return input.selectionStart || 0;
    }
    return 0;
  };

  const handleOnChangeTextAria = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTemplateTextFieldValue(value as string);
    setValue('sendFollowUpMessageTool.messageTemplate', value);
  };

  // open/close modal
  const handleOpenModal = () => {
    const renderedText = Mustache.render(
      mastecheValues.templateTextFieldValue,
      mapMastecheValues(mastecheList),
    );
    setRenderedTextFieldValue(renderedText);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <Grid item xs={12} lg={12}>
      <Box>
        <CustomFormLabel
          sx={{
            mt: '20px',
            fontWeight: 500,
          }}
          htmlFor="campaign-template">
          {t('campaignPage.template')}
        </CustomFormLabel>
        <BorderedGrid
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'start',
            alignItems: 'start',
            marginBottom: 0,
            borderBottomWidth: 0,
            padding: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: theme.palette.extendedGrey?.lightest,
          }}>
          {mastecheList.map((value) => (
            <CustomTooltip
              key={value.key}
              title={getTextFromMastecheTag(value.key, false)}
              placement="top"
              arrow>
              <StyledMastecheButton
                onClick={() => handleAddMastecheValue(value.key)}
                color="primary"
                disabled={disabled}
                variant="text">
                {getTextFromMastecheTag(value.key)}
              </StyledMastecheButton>
            </CustomTooltip>
          ))}
        </BorderedGrid>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            ...(formStateErrors.sendFollowUpMessageTool?.messageTemplate
              ? {
                  '& fieldset': {
                    borderColor: `${theme.palette.error.main} !important`,
                  },
                }
              : {}),
          }}>
          <Controller
            control={toolsControl}
            name="sendFollowUpMessageTool.messageTemplate"
            rules={{ required: requireTemplateTextField }}
            disabled={
              isCampaignActive !== CampaignRunStatusType.INACTIVE || !requireTemplateTextField
            }
            render={({ field }) => (
              <CustomTextField
                {...field}
                sx={{
                  marginRight: '10px',
                  '& .MuiInputBase-multiline': {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    padding: '0px 50px 0px 0px',
                  },
                }}
                value={field.value || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleOnChangeTextAria(e);
                  field.onChange(e.target.value);
                }}
                id="campaign-template"
                variant="outlined"
                fullWidth
                required
                multiline
                rows={5}
              />
            )}
          />
          <IconButton onClick={handleOpenModal}>
            <IconEye />
          </IconButton>
          {formStateErrors.sendFollowUpMessageTool?.messageTemplate && (
            <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
          )}
        </Box>
        <InfoDialog
          showDialogFlag={openModal}
          handleClose={handleCloseModal}
          dialogTitle={t('campaignPage.preview')}
          dialogContentText={renderedTextFieldValue}
          confirmDialog={handleCloseModal}
        />
      </Box>
    </Grid>
  );
};
